import { LABELS } from '@mm/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, ApiError } from '../../api/client';
import { AppIcon, AppIcons } from '../../components/AppIcon';
import { AccountAppsCard } from '../assignments/AccountAppsCard';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Button } from '../../components/ui/Button';
import {
  Alert,
  Card,
  DetailList,
  EmptyState,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../../components/ui/display';
import { DataTable } from '../../tables/DataTable';
import type { AdvertisingAccount, MarketingSummary, PrepaidBalance } from '../../types/models';
import { formatDate, formatMoney, monthName } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import {
  BillingHistoryCard,
  ConvertToCreditLineDialog,
  useBillingChanges,
} from '../billing/BillingChanges';
import { PaymentDialog } from '../billing/components';
import { Stat } from '../marketing/MarketingPage';
import { resourceKey } from './MasterPage';

interface SpendRow {
  expenseBeforeGst: string;
  gstAmount: string;
  totalAmount: string;
}
interface AccountSpend {
  days: (SpendRow & { date: string })[];
  months: (SpendRow & { month: string })[];
  totals: MarketingSummary;
}

const compactInr = (v: number) =>
  v >= 1e7
    ? `₹${(v / 1e7).toFixed(1)}Cr`
    : v >= 1e5
      ? `₹${(v / 1e5).toFixed(1)}L`
      : v >= 1e3
        ? `₹${(v / 1e3).toFixed(0)}k`
        : `₹${v}`;

export function AdvertisingAccountDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const [range, setRange] = useDateFilter('thisMonth');
  const [topUp, setTopUp] = useState(false);
  const [converting, setConverting] = useState(false);
  const changes = useBillingChanges(id);

  const account = useQuery({
    queryKey: [...resourceKey('advertising-accounts'), 'detail', id],
    queryFn: () => api.get<AdvertisingAccount>(`/ads-accounts/${id}`),
  });
  const spend = useQuery({
    queryKey: ['marketing', 'account-spend', id, range.startDate, range.endDate],
    queryFn: () =>
      api.get<AccountSpend>(`/ads-accounts/${id}/spend`, {
        startDate: range.startDate,
        endDate: range.endDate,
      }),
    placeholderData: keepPreviousData,
  });
  const a = account.data;
  const balance = useQuery({
    queryKey: ['billing', 'prepaid', id],
    queryFn: () => api.get<PrepaidBalance>(`/ads-accounts/${id}/prepaid-balance`),
    enabled: a?.billingType === 'PREPAID',
  });

  if (account.isError)
    return (
      <Alert tone="error">
        {account.error instanceof ApiError ? account.error.message : 'Failed to load'}
      </Alert>
    );
  if (!a) return <Spinner />;

  const chartData = (spend.data?.days ?? []).map((d) => ({
    date: d.date.slice(5),
    spend: Number(d.expenseBeforeGst),
    gst: Number(d.gstAmount),
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={
          <Link to="/ads-accounts" className="text-brand-700 hover:underline">
            Ads accounts
          </Link>
        }
        title={
          <span className="flex items-center gap-3">
            <AppIcons apps={(a.assignments ?? []).map((x) => x.app)} size={40} />
            {a.accountName}
          </span>
        }
        description={[a.platform?.name, a.accountIdentifier, a.loginEmail]
          .filter(Boolean)
          .join(' · ')}
        actions={<StatusBadge status={a.status} />}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card title="Account information" className="lg:col-span-2">
          <DetailList
            items={[
              {
                label: 'Used by',
                value: a.assignments?.length ? (
                  <span className="flex flex-wrap gap-x-3 gap-y-1">
                    {a.assignments.map((x) => (
                      <Link
                        key={x.id}
                        to={`/apps/${x.app.id}`}
                        className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
                      >
                        <AppIcon src={x.app.iconUrl} name={x.app.name} size={20} />
                        {x.app.name}
                      </Link>
                    ))}
                  </span>
                ) : (
                  'Not assigned to an app'
                ),
              },
              { label: 'Platform', value: a.platform?.name },
              { label: 'Login email', value: a.loginEmail },
              { label: 'Access email', value: a.accessEmail },
              { label: 'Billing', value: LABELS.billingType[a.billingType] },
              {
                label: 'Manager account (MCC)',
                value: a.managerAccount ? (
                  <Link
                    to={`/mcc-accounts/${a.managerAccount.id}`}
                    className="text-brand-700 hover:underline"
                  >
                    {a.managerAccount.name}
                  </Link>
                ) : (
                  'Not linked'
                ),
              },
              {
                label: 'GST account',
                value: a.gstAccount
                  ? `${a.gstAccount.tradeName || a.gstAccount.legalName} (${a.gstAccount.gstin})`
                  : 'None',
              },
              { label: 'Currency', value: a.currencyCode },
            ]}
          />
        </Card>
        {a.billingType === 'PREPAID' ? (
          <Card
            title={
              <span className="flex items-center gap-2">
                <Wallet className="size-4 text-slate-400" /> Prepaid balance
              </span>
            }
            actions={
              <div className="flex gap-2">
                {can('masters.write') && (
                  <Button size="sm" variant="ghost" onClick={() => setConverting(true)}>
                    Convert to credit line
                  </Button>
                )}
                {can('transactions.write') && (
                  <Button size="sm" variant="secondary" onClick={() => setTopUp(true)}>
                    Top up
                  </Button>
                )}
              </div>
            }
          >
            {balance.data ? (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Opening balance</dt>
                  <dd className="num">{formatMoney(balance.data.openingBalance)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">+ Top-ups</dt>
                  <dd className="num">{formatMoney(balance.data.totalTopups)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">− Spend</dt>
                  <dd className="num">{formatMoney(balance.data.totalSpend)}</dd>
                </div>
                <div className="flex justify-between border-t border-slate-100 pt-2 text-base font-semibold">
                  <dt>Balance</dt>
                  <dd
                    className={`num ${Number(balance.data.balance) < 0 ? 'text-rose-600' : 'text-slate-900'}`}
                  >
                    {formatMoney(balance.data.balance)}
                  </dd>
                </div>
                <p className="text-xs text-slate-500">
                  {balance.data.daysRemaining
                    ? `About ${balance.data.daysRemaining} days left at ${formatMoney(balance.data.averageDailySpend)}/day.`
                    : 'No spend in the last 7 days.'}
                </p>
              </dl>
            ) : (
              <Spinner />
            )}
          </Card>
        ) : (
          <Card title="Credit line">
            <p className="text-sm text-slate-600">
              Spend uses the credit line of{' '}
              {a.managerAccount ? (
                <Link
                  to={`/mcc-accounts/${a.managerAccount.id}`}
                  className="font-medium text-brand-700 hover:underline"
                >
                  {a.managerAccount.name}
                </Link>
              ) : (
                'its MCC'
              )}
              . See the MCC page for credit used and this account's share.
            </p>
            {(() => {
              const left = changes.data?.find(
                (c) =>
                  c.fromType === 'PREPAID' && !c.balanceSettledOn && Number(c.prepaidBalance) !== 0,
              );
              return (
                left && (
                  <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Unused prepaid balance from before {formatDate(left.effectiveFrom)}:{' '}
                    <strong className="num">{formatMoney(left.prepaidBalance)}</strong> (open; see
                    Billing history).
                  </p>
                )
              );
            })()}
          </Card>
        )}
      </div>

      <BillingHistoryCard accountId={a.id} canWrite={can('masters.write')} />

      <AccountAppsCard resource="advertising-accounts" accountId={a.id} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Spend</h2>
        <DateRangeFilter value={range} onChange={setRange} />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Spend (before GST)" value={formatMoney(spend.data?.totals.expenseBeforeGst)} />
        <Stat label="GST" value={formatMoney(spend.data?.totals.gstAmount)} tone="gst" />
        <Stat label="Total spend" value={formatMoney(spend.data?.totals.totalAmount)} />
        <Stat label="Days entered" value={spend.data ? String(spend.data.totals.count) : '—'} />
      </div>

      <Card title="Daily spend">
        {chartData.length === 0 ? (
          <EmptyState title="No spend in this period" />
        ) : (
          <div className="h-64" role="img" aria-label="Daily spend chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={compactInr}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                />
                <Tooltip
                  formatter={(v, name) => [
                    formatMoney(String(v ?? 0)),
                    name === 'spend' ? 'Spend before GST' : 'GST',
                  ]}
                />
                <Bar dataKey="spend" stackId="a" fill="#6366f1" radius={[0, 0, 0, 0]} />
                <Bar dataKey="gst" stackId="a" fill="#a78bfa" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card title="Monthly spend" bodyClassName="p-0">
        <DataTable
          tableId="advertising-account"
          rows={spend.data?.months}
          rowKey={(m) => m.month}
          loading={spend.isLoading}
          columns={[
            {
              key: 'month',
              header: 'Month',
              render: (m) => `${monthName(Number(m.month.slice(5)), true)} ${m.month.slice(0, 4)}`,
            },
            {
              key: 'before',
              header: 'Spend (before GST)',
              align: 'right',
              render: (m) => formatMoney(m.expenseBeforeGst),
            },
            {
              key: 'gst',
              header: 'GST',
              align: 'right',
              render: (m) => <span className="text-violet-700">{formatMoney(m.gstAmount)}</span>,
            },
            {
              key: 'total',
              header: 'Total',
              align: 'right',
              render: (m) => <span className="font-medium">{formatMoney(m.totalAmount)}</span>,
            },
          ]}
        />
      </Card>

      <ConvertToCreditLineDialog
        open={converting}
        onClose={() => setConverting(false)}
        account={a}
      />
      <PaymentDialog
        target={topUp ? { kind: 'prepaid', id: a.id, name: a.accountName } : null}
        onClose={() => setTopUp(false)}
      />
    </div>
  );
}
